"use client";

import * as React from "react";
import { format } from "date-fns";
import { LuCalendar } from "react-icons/lu";
import { TiArrowSortedDown } from "react-icons/ti";

import { cn } from "~/utils/fontsConfig";
import { Calendar, CalendarProps } from "~/components/shadcn/Calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";

type DatePickerProps = CalendarProps & {
    value?: Date;
    onChange?: (date: Date | undefined) => void;
    className?: string;
    contentClassName?: string;
    placeholder?: string;
    theme?: "dark" | "light";
};

export function DatePicker({
    value,
    onChange,
    className,
    contentClassName,
    placeholder = "Pick a date",
    theme = "dark",
    ...props
}: DatePickerProps) {
    const [date, setDate] = React.useState<Date | undefined>(value);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [showIcon, setShowIcon] = React.useState(false);

    React.useEffect(() => {
        setDate(value);
    }, [value]);

    React.useEffect(() => {
        const checkWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current?.clientWidth || 0;
                setShowIcon(width > 160);
            }
        };

        checkWidth();
        window.addEventListener("resize", checkWidth);
        return () => window.removeEventListener("resize", checkWidth);
    }, []);

    const handleSelect = (selectedDate: Date | undefined) => {
        if (selectedDate) {
            setDate(selectedDate);
            onChange?.(selectedDate);
        }
        containerRef.current?.click();
    };

    const getDateString = () => {
        if (date) {
            const width = containerRef.current?.clientWidth || 0;
            if (width > 160) return format(date, "EEE, MMM d");
            if (width > 120) return format(date, "MM/dd/yy");
            return format(date, "MM/dd");
        }

        return placeholder;
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div
                    ref={containerRef}
                    className={cn(
                        "flex cursor-pointer items-center justify-between rounded-[0.625rem] bg-v3-N-500 p-2 px-4 text-left font-normal",
                        !date && "text-muted-foreground",
                        className,
                        theme === "light" && "bg-white",
                    )}
                >
                    {showIcon && <LuCalendar className="mr-2 h-4 w-4 " />}
                    <span
                        className={cn(
                            "text-v3-N-300",
                            theme === "light" && "text-black",
                        )}
                    >
                        {getDateString()}
                    </span>
                    <TiArrowSortedDown className="h-4 w-4" />
                </div>
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    "w-auto p-0",
                    contentClassName,
                    theme === "light" && "bg-white",
                )}
                align="start"
            >
                <Calendar
                    {...props}
                    mode="single"
                    selected={date}
                    onSelect={handleSelect}
                    autoFocus
                    defaultMonth={date}
                    theme={theme}
                />
            </PopoverContent>
        </Popover>
    );
}
